import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Copy, Check, Loader2, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getPublicChargeInfo } from "@/lib/api/charges.functions";

export const Route = createFileRoute("/pix/$id")({
  head: () => ({
    meta: [
      { title: "Pagamento Pix" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PixPage,
});

const fmtBRL = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copiado!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };
  return (
    <Button onClick={copy} className="w-full" size="lg">
      {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
      {label}
    </Button>
  );
}

function PixPage() {
  const { id } = Route.useParams();
  const load = useServerFn(getPublicChargeInfo);
  const [info, setInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    load({ data: { id } })
      .then((r: any) => setInfo(r))
      .catch((e: any) => setErr(e?.message || "Erro ao carregar"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (err || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">{err || "Cobrança não encontrada."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="py-8 space-y-6">
          <div className="text-center">
            <QrCode className="h-10 w-10 text-primary mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Pagamento Pix</h1>
            <p className="text-sm text-muted-foreground">{info.ct_name}</p>
            {info.student_first_name && (
              <p className="text-xs text-muted-foreground mt-1">
                Referente a: {info.student_first_name}
              </p>
            )}
          </div>

          <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Valor</div>
            <div className="text-3xl font-bold text-primary">{fmtBRL(info.amount)}</div>
            {info.status === "confirmado" && (
              <div className="text-xs text-green-600 font-medium mt-1">✓ Pago</div>
            )}
          </div>

          {info.pix_key && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Chave Pix</div>
              <div className="font-mono text-sm bg-muted p-3 rounded-md break-all">
                {info.pix_key}
              </div>
              {info.pix_beneficiary && (
                <div className="text-xs text-muted-foreground">
                  Favorecido: {info.pix_beneficiary}
                </div>
              )}
              <CopyButton value={info.pix_key} label="Copiar chave Pix" />
            </div>
          )}

          {info.brcode && (
            <div className="space-y-2 pt-2 border-t">
              <div className="text-xs font-medium text-muted-foreground">
                Pix copia-e-cola (com valor)
              </div>
              <div className="font-mono text-xs bg-muted p-3 rounded-md break-all max-h-24 overflow-y-auto">
                {info.brcode}
              </div>
              <CopyButton value={info.brcode} label="Copiar Pix copia-e-cola" />
              <p className="text-xs text-muted-foreground text-center">
                Cole no seu app do banco em "Pix copia-e-cola".
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center pt-2">
            Após pagar, envie o comprovante no mesmo WhatsApp.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
